"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation, useQuery } from "convex/react";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckSquare,
  FolderKanban,
  Loader2,
  Sparkles,
  X,
} from "lucide-react";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { MicButton } from "@/components/ui/MicButton";
import { useClientContext } from "@/context/ClientContext";

type Tab = "task" | "project" | "client" | "ai";

type Priority = "low" | "medium" | "high" | "urgent";

const PRIORITIES: Priority[] = ["low", "medium", "high", "urgent"];

const TABS: { key: Tab; label: string; icon: typeof CheckSquare }[] = [
  { key: "task", label: "Task", icon: CheckSquare },
  { key: "project", label: "Project", icon: FolderKanban },
  { key: "client", label: "Client", icon: Building2 },
  { key: "ai", label: "AI Request", icon: Sparkles },
];

function slugify(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function NewItemModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>("task");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:p-8">
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative z-10 mt-8 w-full max-w-xl rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <h2 className="text-base font-semibold text-foreground">Create new</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex border-b border-border px-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "flex flex-1 items-center justify-center gap-1.5 border-b-2 px-3 py-2.5 text-sm transition-colors",
                  active
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {tab === "task" && <TaskForm onDone={onClose} />}
          {tab === "project" && <ProjectForm onDone={onClose} />}
          {tab === "client" && <ClientForm onDone={onClose} />}
          {tab === "ai" && <AIRequestForm onDone={onClose} />}
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
      {children}
    </span>
  );
}

function NativeSelect({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <select
      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {children}
    </select>
  );
}

function ErrorMessage({ message }: { message?: string }) {
  if (!message) return null;
  return <div className="text-xs text-red-400">{message}</div>;
}

function TaskForm({ onDone }: { onDone: () => void }) {
  const router = useRouter();
  const { clientId: activeClientId, isAgencyView } = useClientContext();
  const team = useQuery(api.team.list);
  const allProjects = useQuery(api.projects.list);
  const createTask = useMutation(api.tasks.create);

  // When scoped to a client, only show that client's projects
  const projects = !isAgencyView && activeClientId
    ? allProjects?.filter((p) => String(p.clientId) === String(activeClientId))
    : allProjects;

  const [title, setTitle] = useState("");
  const [projectId, setProjectId] = useState<string>("");
  const [assigneeId, setAssigneeId] = useState<string>("");
  const [priority, setPriority] = useState<Priority>("medium");
  const [dueDate, setDueDate] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      const id = await createTask({
        title: title.trim(),
        description: description.trim() || undefined,
        status: "inbox",
        priority,
        assigneeId: assigneeId ? (assigneeId as Id<"teamMembers">) : undefined,
        projectId: projectId ? (projectId as Id<"projects">) : undefined,
        dueDate: dueDate ? new Date(dueDate).getTime() : undefined,
      });
      onDone();
      router.push(`/tasks/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create task");
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <div className="space-y-1.5">
        <FieldLabel>Title *</FieldLabel>
        <div className="relative flex items-center">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to happen?"
            autoFocus
            className="pr-8"
          />
          <MicButton
            onTranscript={(text) =>
              setTitle((prev) => (prev ? `${prev} ${text}` : text))
            }
            className="absolute right-1"
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1.5">
          <FieldLabel>Project</FieldLabel>
          <NativeSelect value={projectId} onChange={setProjectId}>
            <option value="">No project</option>
            {projects?.map((project) => (
              <option key={project._id} value={project._id}>{project.title}</option>
            ))}
          </NativeSelect>
        </label>
        <label className="space-y-1.5">
          <FieldLabel>Assignee</FieldLabel>
          <NativeSelect value={assigneeId} onChange={setAssigneeId}>
            <option value="">Unassigned</option>
            {team?.filter((m) => m.status !== "inactive").map((member) => (
              <option key={member._id} value={member._id}>{member.name}</option>
            ))}
          </NativeSelect>
        </label>
        <label className="space-y-1.5">
          <FieldLabel>Priority</FieldLabel>
          <NativeSelect value={priority} onChange={(v) => setPriority(v as Priority)}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p} className="capitalize">{p}</option>
            ))}
          </NativeSelect>
        </label>
        <label className="space-y-1.5">
          <FieldLabel>Due date</FieldLabel>
          <input
            type="date"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </label>
      </div>

      <div className="space-y-1.5">
        <FieldLabel>Description</FieldLabel>
        <div className="relative">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional context, success criteria, links."
            className="min-h-24 bg-background pb-7"
          />
          <MicButton
            onTranscript={(text) =>
              setDescription((prev) => (prev ? `${prev} ${text}` : text))
            }
            className="absolute bottom-1.5 right-1.5"
          />
        </div>
      </div>

      <ErrorMessage message={error} />

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onDone} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !title.trim()}>
          {submitting ? "Creating…" : "Create task"}
        </Button>
      </div>
    </form>
  );
}

function ProjectForm({ onDone }: { onDone: () => void }) {
  const router = useRouter();
  const { clientId: activeClientId, isAgencyView, currentClient } = useClientContext();
  const clients = useQuery(api.clients.list);
  const createProject = useMutation(api.projects.create);

  // Pre-fill client when scoped to a client context
  const scopedToClient = !isAgencyView && !!activeClientId;
  const [title, setTitle] = useState("");
  const [clientId, setClientId] = useState<string>(
    scopedToClient ? String(activeClientId) : "",
  );
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!clientId) {
      setError("Pick a client (or create one first)");
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      const id = await createProject({
        title: title.trim(),
        description: description.trim() || undefined,
        status: "active",
        clientId: clientId as Id<"clients">,
      });
      onDone();
      router.push(`/projects/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create project");
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <label className="space-y-1.5">
        <FieldLabel>Title *</FieldLabel>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Project name"
          autoFocus
        />
      </label>

      {scopedToClient ? (
        <div className="space-y-1.5">
          <FieldLabel>Client</FieldLabel>
          <div className="rounded-md border border-border bg-background px-3 py-2 text-sm text-muted-foreground">
            {currentClient?.name ?? "Selected client"}
          </div>
        </div>
      ) : (
        <label className="space-y-1.5">
          <FieldLabel>Client *</FieldLabel>
          <NativeSelect value={clientId} onChange={setClientId}>
            <option value="">Select a client</option>
            {clients?.map((client) => (
              <option key={client._id} value={client._id}>{client.name}</option>
            ))}
          </NativeSelect>
        </label>
      )}

      <label className="space-y-1.5">
        <FieldLabel>Description</FieldLabel>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this project about?"
          className="min-h-24 bg-background"
        />
      </label>

      <ErrorMessage message={error} />

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onDone} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !title.trim() || !clientId}>
          {submitting ? "Creating…" : "Create project"}
        </Button>
      </div>
    </form>
  );
}

function ClientForm({ onDone }: { onDone: () => void }) {
  const router = useRouter();
  const createClient = useMutation(api.clients.create);

  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [summary, setSummary] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setError(undefined);
    setSubmitting(true);
    try {
      await createClient({
        slug: slugify(name),
        name: name.trim(),
        status: "active",
        type: "other",
        website: website.trim() || undefined,
        summary: summary.trim() || undefined,
      });
      onDone();
      router.push("/clients");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create client");
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <label className="space-y-1.5">
        <FieldLabel>Name *</FieldLabel>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Client name"
          autoFocus
        />
      </label>

      <label className="space-y-1.5">
        <FieldLabel>Website</FieldLabel>
        <Input
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://…"
        />
      </label>

      <label className="space-y-1.5">
        <FieldLabel>Summary</FieldLabel>
        <Textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="One-liner about who they are and what they do."
          className="min-h-24 bg-background"
        />
      </label>

      <ErrorMessage message={error} />

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onDone} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !name.trim()}>
          {submitting ? "Creating…" : "Create client"}
        </Button>
      </div>
    </form>
  );
}

// ── Types for classify-request API response ───────────────────────────
type ContentType = "one-pager" | "deck" | "case-study" | "product-sheet" | "other";

interface ClassifyResponse {
  isSalesContent: boolean;
  contentType?: ContentType;
  product?: string | null;
  audience?: string | null;
  industry?: string | null;
}

const CONTENT_TYPE_OPTIONS: { value: ContentType; label: string }[] = [
  { value: "one-pager", label: "One-Pager" },
  { value: "deck", label: "Sales Deck" },
  { value: "product-sheet", label: "Product Sheet" },
  { value: "case-study", label: "Case Study" },
  { value: "other", label: "Other" },
];

function truncateTitle(str: string, max = 60): string {
  if (str.length <= max) return str;
  return str.slice(0, max).replace(/\s\S+$/, '') + '…';
}

function AIRequestForm({ onDone }: { onDone: () => void }) {
  const router = useRouter();
  const { clientId: activeClientId, isAgencyView } = useClientContext();
  const team = useQuery(api.team.list);
  const allProjects = useQuery(api.projects.list);
  const createTask = useMutation(api.tasks.create);

  // When scoped to a client, only show that client's projects
  const projects = !isAgencyView && activeClientId
    ? allProjects?.filter((p) => String(p.clientId) === String(activeClientId))
    : allProjects;

  // Auto-select the first matching project; let user override via dropdown
  const autoProjectId = useMemo(() => {
    if (isAgencyView || !activeClientId || !projects) return "";
    return projects[0]?._id ?? "";
  }, [isAgencyView, activeClientId, projects]);

  const [projectId, setProjectId] = useState<string>("");

  // Sync default once project list loads
  useEffect(() => {
    if (autoProjectId && !projectId) setProjectId(String(autoProjectId));
  }, [autoProjectId]);

  const hal = useMemo(
    () => team?.find((m) => m.name.toLowerCase() === "hal" && m.isAgent),
    [team],
  );

  // ── Step tracking ───────────────────────────────────────────────────
  const [step, setStep] = useState<1 | 2>(1);
  const [classifying, setClassifying] = useState(false);

  // ── Step 1 state ────────────────────────────────────────────────────
  const [text, setText] = useState("");

  // ── Step 2 state (pre-filled from classify response) ────────────────
  const [contentType, setContentType] = useState<ContentType>("one-pager");
  const [productField, setProductField] = useState("");
  const [audienceField, setAudienceField] = useState("");
  const [goalField, setGoalField] = useState("");
  const [sourceUrls, setSourceUrls] = useState("");

  // ── Shared state ────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedTaskId, setSubmittedTaskId] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>();

  // ── Helpers ─────────────────────────────────────────────────────────

  /** Create the task + dispatch Hal in work mode */
  async function createAndDispatch(args: {
    title: string;
    description: string;
    status: "inbox" | "working";
    priority: "medium" | "high";
  }) {
    const taskId = await createTask({
      title: args.title,
      description: args.description,
      status: args.status,
      priority: args.priority,
      assigneeId: hal?._id,
      projectId: projectId ? (projectId as Id<"projects">) : undefined,
    });

    // Dispatch Hal in work mode
    try {
      await fetch("/api/notify-agent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          taskId: String(taskId),
          mentionedAgent: "hal",
          taskTitle: args.title,
          commentText: args.description,
          commenterName: "Joel",
          mode: "work",
        }),
      });
    } catch (e) {
      // Non-fatal: task is created, agent dispatch is best-effort
      console.error("Failed to dispatch Hal:", e);
    }

    return taskId;
  }

  // ── Step 1: Classify & route ────────────────────────────────────────

  const handleStep1Submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!text.trim()) {
      setError("Describe what you need");
      return;
    }
    setError(undefined);
    setClassifying(true);

    try {
      // Classify the request
      let classification: ClassifyResponse = { isSalesContent: false };
      try {
        const res = await fetch("/api/classify-request", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: text.trim() }),
        });
        if (res.ok) {
          classification = await res.json();
        }
      } catch {
        // Classification failed — degrade to non-content path
      }

      if (classification.isSalesContent) {
        // Pre-fill Step 2 fields from extracted data
        if (classification.contentType) setContentType(classification.contentType);
        if (classification.product) setProductField(classification.product);
        if (classification.audience) setAudienceField(classification.audience);
        setClassifying(false);
        setStep(2);
      } else {
        // Non-content request: create task + dispatch immediately
        setClassifying(false);
        setSubmitting(true);
        const trimmed = text.trim();
        const taskId = await createAndDispatch({
          title: truncateTitle(trimmed),
          description: trimmed,
          status: "working",
          priority: "medium",
        });
        setSubmittedTaskId(String(taskId));
        setSubmitted(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit request");
      setClassifying(false);
      setSubmitting(false);
    }
  };

  // ── Step 2: Submit structured content request ───────────────────────

  const handleStep2Submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setSubmitting(true);

    try {
      const typeLabel =
        CONTENT_TYPE_OPTIONS.find((o) => o.value === contentType)?.label ?? contentType;

      // Build title: "[ContentType]: [Product] for [Audience]" — max 60 chars
      const titleParts = [typeLabel];
      if (productField.trim()) titleParts.push(productField.trim());
      if (audienceField.trim()) titleParts.push(`for ${audienceField.trim()}`);
      const title = truncateTitle(titleParts.join(": "));

      // Build structured description
      const descriptionLines = [
        "## Content Request",
        `**Type:** ${typeLabel}`,
      ];
      if (productField.trim())
        descriptionLines.push(`**Product / Topic:** ${productField.trim()}`);
      if (audienceField.trim())
        descriptionLines.push(`**Audience:** ${audienceField.trim()}`);
      if (goalField.trim())
        descriptionLines.push(`**Goal:** ${goalField.trim()}`);
      if (sourceUrls.trim()) {
        descriptionLines.push("**Sources:**");
        const lines = sourceUrls
          .trim()
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        for (const line of lines) {
          descriptionLines.push(`- ${line}`);
        }
      }
      descriptionLines.push("");
      descriptionLines.push("---");
      descriptionLines.push(`_Original request: "${text.trim()}"_`);

      const description = descriptionLines.join("\n");

      const taskId = await createAndDispatch({
        title,
        description,
        status: "working",
        priority: "high",
      });
      setSubmittedTaskId(String(taskId));
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit request");
      setSubmitting(false);
    }
  };

  // ── Submitted success state ─────────────────────────────────────────

  if (submitted) {
    const isContentRequest = step === 2;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-3 text-sm text-foreground">
          <Sparkles className="h-4 w-4" />
          {isContentRequest
            ? "Hal is on it — you\u2019ll see updates in the task thread."
            : "Request submitted — Hal is working on it now."}
        </div>
        <div className="flex justify-end gap-2">
          {submittedTaskId && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                onDone();
                router.push(`/tasks/${submittedTaskId}`);
              }}
            >
              View task
            </Button>
          )}
          <Button type="button" onClick={onDone}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  // ── Step 1: Describe what you need ──────────────────────────────────

  if (step === 1) {
    return (
      <form className="space-y-3" onSubmit={handleStep1Submit}>
        <div className="space-y-1.5">
          <FieldLabel>Describe what you need…</FieldLabel>
          <div className="relative">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder='e.g. "Generate a one-pager for the LS Series targeting channel partners."'
              className="min-h-32 bg-background pb-7"
              autoFocus
              disabled={classifying || submitting}
            />
            <MicButton
              onTranscript={(t) =>
                setText((prev) => (prev ? `${prev} ${t}` : t))
              }
              className="absolute bottom-1.5 right-1.5"
            />
          </div>
        </div>

        {!hal && team && (
          <div className="text-xs text-amber-400">
            Hal isn&apos;t set up as a team member yet — the task will be created
            unassigned.
          </div>
        )}

        <ErrorMessage message={error} />

        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="ghost"
            onClick={onDone}
            disabled={classifying || submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={classifying || submitting || !text.trim()}
          >
            {classifying ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Analyzing…
              </>
            ) : submitting ? (
              "Submitting…"
            ) : (
              <>
                Next
                <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </>
            )}
          </Button>
        </div>
      </form>
    );
  }

  // ── Step 2: Structured content form ─────────────────────────────────

  return (
    <form className="space-y-3" onSubmit={handleStep2Submit}>
      {/* Content type pills */}
      <div className="space-y-1.5">
        <FieldLabel>Content Type</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {CONTENT_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setContentType(opt.value)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                contentType === opt.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Product / Series / Industry */}
      <label className="block space-y-1.5">
        <FieldLabel>Product / Series / Industry</FieldLabel>
        <Input
          value={productField}
          onChange={(e) => setProductField(e.target.value)}
          placeholder="e.g. LS Series, Smart Cities vertical, G520"
          disabled={submitting}
          list="lantronix-topics"
        />
        <datalist id="lantronix-topics">
          <option value="LS Series" />
          <option value="G520 Series" />
          <option value="SMTATSA Series" />
          <option value="EDS Series" />
          <option value="SLC 8000 Series" />
          <option value="Enterprise Switches" />
          <option value="Hardened Switches" />
          <option value="Smart Cities" />
          <option value="Aerospace & Defense" />
          <option value="Industrial Networking" />
          <option value="Healthcare" />
          <option value="Retail & Hospitality" />
          <option value="Government / SLED" />
        </datalist>
      </label>

      {/* Audience */}
      <label className="block space-y-1.5">
        <FieldLabel>Audience</FieldLabel>
        <Input
          value={audienceField}
          onChange={(e) => setAudienceField(e.target.value)}
          placeholder="e.g. Channel partners, IT Directors, Network Engineers"
          disabled={submitting}
        />
      </label>

      {/* Goal */}
      <label className="block space-y-1.5">
        <FieldLabel>Goal</FieldLabel>
        <Input
          value={goalField}
          onChange={(e) => setGoalField(e.target.value)}
          placeholder="e.g. Leave-behind for ACME meeting, cold outreach, trade show"
          disabled={submitting}
        />
      </label>

      {/* Project */}
      {projects && projects.length > 0 && (
        <label className="block space-y-1.5">
          <FieldLabel>Project</FieldLabel>
          <NativeSelect value={projectId} onChange={setProjectId}>
            <option value="">No project</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>{p.title}</option>
            ))}
          </NativeSelect>
        </label>
      )}

      {/* Source URLs */}
      <div className="space-y-1.5">
        <FieldLabel>
          Product pages, data sheets, or other sources (one per line — or describe
          them in plain English)
        </FieldLabel>
        <Textarea
          value={sourceUrls}
          onChange={(e) => setSourceUrls(e.target.value)}
          placeholder={`e.g. https://lantronix.com/products/ls-series/\nAlso pull from the LS Series datasheet`}
          className="min-h-20 bg-background"
          disabled={submitting}
        />
      </div>

      <ErrorMessage message={error} />

      <div className="flex justify-between gap-2 pt-1">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setStep(1);
            setError(undefined);
          }}
          disabled={submitting}
        >
          <ArrowLeft className="mr-1 h-3.5 w-3.5" />
          Back
        </Button>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onDone}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Creating…
              </>
            ) : (
              <>
                Generate
                <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </>
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
